import { Cushion, FlexContainer, Grid, Rectangle, Space, Typography } from '@pitchero/react-ui';
import { find, sortBy } from 'lodash';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import Icon from '../icon';
import { Trans, withTranslation } from '../../lib/i18n';
import Nl2Br from '../nl2br';
import ClubLink from '../club-link';

const OverviewLink = styled.div`
  width: 100%;
  box-sizing: border-box;
  background-color: white;
  border-radius: 100px;
  border: 2px solid lightgray;
  text-transform: uppercase;
  font-weight: bold;
  text-align: center;
  font-size: 14px;
  line-height: 18px;
  padding: 10px 10px;
  transition: transform 300ms ease-in-out;
  cursor: pointer;
  &:hover {
    transform: scale(1.05);
  }
`;

const MatchDetails = ({ overview, location, table, teamId, club }) => {
  const { attendance, instructions, meetTime, kickoff } = overview;
  const date = DateTime.fromISO(overview.date);
  const leaguePositions = [];

  if (table) {
    const homeRow = find(table.rows, (row) => {
      const nameCol = find(row.column_values, { key: 'team_name' });
      return nameCol && nameCol.value === overview.home.name;
    });
    const awayRow = find(table.rows, (row) => {
      const nameCol = find(row.column_values, { key: 'team_name' });
      return nameCol && nameCol.value === overview.away.name;
    });

    if (homeRow) {
      leaguePositions.push({
        key: 'leaguePosH',
        position: homeRow.rank,
        name: overview.home.name,
      });
    }

    if (awayRow) {
      leaguePositions.push({
        key: 'leaguePosA',
        position: awayRow.rank,
        name: overview.away.name,
      });
    }
  }

  const validCoords =
    location &&
    typeof location.lat === 'number' &&
    typeof location.lng === 'number' &&
    !(location.lat === 0 && location.lng === 0);

  const renderLeaguePosition = (position, name) => (
    <Space bottom="small" key={`leaguePos${position}`}>
      <FlexContainer alignItems="center" style={{ gap: '10px' }}>
        <Rectangle fill="#fff" radius="rounded" style={{ border: '1px solid #ccc' }}>
          <Cushion all="xsmall">
            <Typography
              preset="name"
              align="center"
              size={16}
              style={{ width: 20, display: 'inline-block' }}
            >
              {position}
            </Typography>
          </Cushion>
        </Rectangle>
        <Typography preset="body--large" component="div">
          {name}
        </Typography>
      </FlexContainer>
    </Space>
  );

  const subHeading = (i18nKey, text) => (
    <Cushion bottom="small">
      <Typography preset="subtitle--small" color="dustygrey" component="h4">
        <Trans i18nKey={`teams:${i18nKey}`}>{text}</Trans>
      </Typography>
    </Cushion>
  );

  return (
    <>
      <Rectangle fill="white" radius="8px" style={{ overflow: 'hidden', margin: '0 12px' }}>
        <FlexContainer alignItems="center" style={{ backgroundColor: '#000' }}>
          <Cushion horizontal="medium" vertical="small">
            <Typography color="#fff" size={18} weight="bold" component="h3">
              <Trans i18nKey="teams:match_details_title">Match Details</Trans>
            </Typography>
          </Cushion>
        </FlexContainer>
        <Cushion
          all="medium"
          style={{ border: '1px solid #b8b8b8', borderRadius: '0 0 8px 8px', borderTop: '0' }}
        >
          <FlexContainer flexDirection="column" style={{ gap: '30px' }}>
            <div>
              {subHeading('match_date_label', 'Match Date')}
              <Grid preset="icon-label">
                <Icon name="calendar" size={27} color="primary" />
                <Typography preset="body--large" component="div">
                  {date.toFormat('ccc dd LLL yyyy')}
                </Typography>
              </Grid>
            </div>

            <div>
              {subHeading(`kickoff_label_${club.sport}`, 'Kickoff')}
              <Grid preset="icon-label">
                <Icon name="clock" size={27} color="primary" />
                <Typography preset="body--large" component="div">
                  {kickoff}
                </Typography>
              </Grid>
            </div>
            {meetTime && (
              <div>
                {subHeading('meet_time_label', 'Meet Time')}
                <Grid preset="icon-label">
                  <Icon name="clock" size={27} color="primary" />
                  <Typography preset="body--large" component="div">
                    {meetTime}
                  </Typography>
                </Grid>
              </div>
            )}
            {validCoords && (
              <div>
                {subHeading('location_label', 'Location')}
                <Grid preset="icon-label">
                  <Icon name="location" size={27} color="primary" />
                  <Typography
                    preset="body--large"
                    style={{ textDecoration: 'underline' }}
                    component="a"
                    href={`https://maps.google.com/maps?z=12&t=m&q=${location.lat},${location.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {location.name}
                  </Typography>
                </Grid>
              </div>
            )}
            {instructions && (
              <div>
                {subHeading('instructions_label', 'Instructions')}
                <Grid preset="icon-label">
                  <Icon name="page" size={27} color="primary" />
                  <Typography preset="body--large" component="div">
                    <Nl2Br>{instructions}</Nl2Br>
                  </Typography>
                </Grid>
              </div>
            )}
            {attendance && (
              <div>
                {subHeading('attendance_label', 'Attendance')}
                <Grid preset="icon-label">
                  <Icon name="users" size={27} color="primary" />
                  <Typography preset="body--large" component="div">
                    {attendance.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  </Typography>
                </Grid>
              </div>
            )}
            {overview.competition && (
              <div>
                {subHeading('competition_label', 'Competition')}
                <Grid preset="icon-label">
                  <Icon name="trophy" size={27} color="primary" />
                  <Typography preset="body--large" component="div">
                    {overview.competition}
                  </Typography>
                </Grid>
              </div>
            )}
            {leaguePositions.length > 0 && (
              <div>
                {subHeading('league_position_label', 'League Position')}
                {sortBy(leaguePositions, ['position']).map((leaguePosition) =>
                  renderLeaguePosition(leaguePosition.position, leaguePosition.name),
                )}
              </div>
            )}

            <ClubLink route="team.homepage" params={{ teamId }}>
              <OverviewLink>
                <Trans i18nKey="teams:match_centre_team_homepage_link">Team Overview</Trans>
              </OverviewLink>
            </ClubLink>
          </FlexContainer>
        </Cushion>
      </Rectangle>
    </>
  );
};

MatchDetails.defaultProps = {
  table: null,
  location: null,
  club: null,
};

MatchDetails.propTypes = {
  overview: PropTypes.shape({
    attendance: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    away: PropTypes.shape({
      name: PropTypes.string,
    }),
    competition: PropTypes.string,
    date: PropTypes.string.isRequired,
    home: PropTypes.shape({
      name: PropTypes.string,
    }),
    kickoff: PropTypes.string,
    instructions: PropTypes.string,
    meetTime: PropTypes.string,
    playersOfTheMatch: PropTypes.arrayOf(
      PropTypes.shape({
        avatar: PropTypes.string,
        id: PropTypes.number,
        name: PropTypes.string,
      }),
    ),
  }).isRequired,
  location: PropTypes.shape(),
  table: PropTypes.shape({
    rows: PropTypes.arrayOf(
      PropTypes.shape({
        column_values: PropTypes.arrayOf(
          PropTypes.shape({
            key: PropTypes.string,
            value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
          }),
        ),
      }),
    ),
  }),
  teamId: PropTypes.number.isRequired,
  club: PropTypes.shape({
    sport: PropTypes.string.isRequired,
  }),
};

export default withTranslation(['teams'])(MatchDetails);
